import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppHeader, SourceNote } from "@/components/Layout";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { formatDate, imageUrl, readingTime } from "@/lib/blog";
import { Clock } from "lucide-react";

export default function BlogIndex() {
  useSeo({
    title: "Food safety blog",
    description:
      "In-depth guides on Australian food recalls, allergen safety and household food-safety practices from the Food Recall Alerts team.",
    path: "/blog",
  });

  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("");

  useEffect(() => {
    api.get("/blog/posts").then(setPosts).catch(() => setPosts([]));
    api.get("/blog/categories").then(setCats).catch(() => setCats([]));
  }, []);

  const filtered = activeCat
    ? posts.filter((p) => p.category?.slug === activeCat)
    : posts;

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <div className="mx-auto max-w-5xl px-5 py-10 sm:py-14" data-testid="blog-index">
        <header className="mb-8">
          <p className="label-caps text-red-700">Blog</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            Food safety, explained.
          </h1>
          <p className="mt-3 max-w-2xl text-slate-600">
            Long-form guides and context on Australian food recalls, allergens, and the day-to-day
            food-safety decisions families make.
          </p>
        </header>

        <nav className="mb-8 flex flex-nowrap gap-2 overflow-x-auto pb-1" data-testid="blog-category-chips">
          <button
            data-testid="chip-all"
            onClick={() => setActiveCat("")}
            className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
              activeCat === "" ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200"
            }`}
          >
            All
          </button>
          {cats.map((c) => (
            <button
              key={c.id}
              data-testid={`chip-${c.slug}`}
              onClick={() => setActiveCat(c.slug)}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                activeCat === c.slug ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200"
              }`}
            >
              {c.name}
            </button>
          ))}
        </nav>

        {filtered.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="blog-empty">
            <p className="text-slate-500">No posts yet. New guides land here as we publish them.</p>
          </div>
        ) : (
          <ul className="space-y-6">
            {filtered.map((p) => (
              <li key={p.id} data-testid={`blog-card-${p.slug}`}
                  className="group grid gap-6 overflow-hidden rounded-2xl border border-slate-200 bg-white sm:grid-cols-[220px_1fr]">
                <Link to={`/blog/${p.slug}`} className="block sm:aspect-auto">
                  {p.hero_image ? (
                    <img src={imageUrl(p.hero_image)} alt={p.hero_alt || p.title}
                         className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                  ) : (
                    <div className="h-full min-h-[160px] w-full bg-gradient-to-br from-red-700 to-red-900" />
                  )}
                </Link>
                <div className="flex flex-col justify-between gap-4 p-5">
                  <div>
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      {p.category && (
                        <Link to={`/blog/category/${p.category.slug}`}
                              className="rounded-full px-2.5 py-0.5 font-semibold uppercase tracking-wide"
                              style={{ backgroundColor: `${p.category.color}22`, color: p.category.color }}>
                          {p.category.name}
                        </Link>
                      )}
                      <span className="text-slate-500">{formatDate(p.publish_date)}</span>
                      <span className="inline-flex items-center gap-1 text-slate-500">
                        <Clock className="h-3.5 w-3.5" /> {p.reading_minutes || readingTime(p.body)} min read
                      </span>
                    </div>
                    <Link to={`/blog/${p.slug}`}
                          className="mt-3 block font-display text-2xl font-bold tracking-tight text-slate-900 group-hover:text-red-700">
                      {p.title}
                    </Link>
                    <p className="mt-2 line-clamp-2 text-slate-600">{p.excerpt}</p>
                  </div>
                  {p.author && (
                    <Link to={`/blog/author/${p.author.slug}`} className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900">
                      {p.author.avatar_url && (
                        <img src={imageUrl(p.author.avatar_url)} alt=""
                             className="h-6 w-6 rounded-full object-cover" />
                      )}
                      By {p.author.name}
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <SourceNote className="mt-12" />
      </div>
    </div>
  );
}
